import React, { useEffect, useState } from 'react';

import { getRecentSearches, removeRecentSearch } from '../../../../util/searchSubmit';

import css from './SearchOverlayContent.module.css';

// Gold AI sparkle — 4-point star (same path family as the search fields'
// StarIcon) with 2 satellite stars, per the approved Combo C reference
// (design-reference/mobile-search-overlay.html).
const SparkleIcon = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 3l1.4 4.1L15.5 8.5l-4.1 1.4L10 14l-1.4-4.1L4.5 8.5l4.1-1.4L10 3z" />
    <path d="M18.5 12.5v4M16.5 14.5h4" />
    <path d="M7.5 17.5v3M6 19h3" />
  </svg>
);

// Muted ⟲-style history glyph for recent rows.
const HistoryIcon = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
  </svg>
);

const RemoveIcon = ({ className }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

// Editorial example queries (STATE 1 cards / STATE 2 chips) — approved copy.
const SHOWCASE_PROMPTS = [
  'Barrel horse under $40k with papers',
  'Gelding by Dash Ta Fame',
  'Finished rope horse in Texas',
];
const STRIP_CHIPS = ['Barrel horse under $40k', 'Gelding by Dash Ta Fame'];

/**
 * Body of the mobile full-screen search overlay (Combo C, adaptive):
 * STATE 1 (no stored recents) — centered AI showcase with tappable example
 * prompt cards; STATE 2 (has recents) — compact dark "Just ask." band with
 * example chips over the RECENT list. Recents live in localStorage
 * ('tphRecentSearches', written by util/searchSubmit) and are re-read each
 * time the overlay opens; removing the last one reverts to STATE 1.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen whether the overlay is currently open
 * @param {Function} props.onQuerySelect (query) => void — runs the query
 *   through the shared route-at-submit orchestration and closes the overlay
 */
const SearchOverlayContent = props => {
  const { isOpen, onQuerySelect } = props;
  const [recents, setRecents] = useState([]);

  // Refresh from storage on every open — a search submitted elsewhere (hero,
  // overlay input) since the last open must show up.
  useEffect(() => {
    if (isOpen) {
      setRecents(getRecentSearches());
    }
  }, [isOpen]);

  const handleRemove = query => {
    removeRecentSearch(query);
    // Derive the next list from state (not storage) so the row disappears even
    // when localStorage is unavailable; last removal reverts to STATE 1.
    setRecents(prev => prev.filter(s => s.toLowerCase() !== query.toLowerCase()));
  };

  if (recents.length === 0) {
    // STATE 1 — first-time editorial showcase.
    return (
      <div className={css.root}>
        <div className={css.showcase}>
          <SparkleIcon className={css.showcaseSparkle} />
          <h2 className={css.showcaseHead}>
            Just ask.
            <em>We&rsquo;ll find the horse.</em>
          </h2>
          <p className={css.showcaseBody}>
            Describe what you&rsquo;re after the way you&rsquo;d tell a friend &mdash; discipline,
            budget, bloodlines, location.
          </p>
        </div>
        {SHOWCASE_PROMPTS.map(prompt => (
          <button
            key={prompt}
            type="button"
            className={css.promptCard}
            onClick={() => onQuerySelect(prompt)}
          >
            <span className={css.promptSparkle} aria-hidden="true">
              ✦
            </span>
            &ldquo;{prompt}&rdquo;
          </button>
        ))}
      </div>
    );
  }

  // STATE 2 — compact AI band + recent searches.
  return (
    <div className={css.root}>
      <div className={css.aiStrip}>
        <div className={css.aiStripHead}>
          Just ask. <em>We&rsquo;ll find the horse.</em>
        </div>
        {STRIP_CHIPS.map(chip => (
          <button
            key={chip}
            type="button"
            className={css.chipDark}
            onClick={() => onQuerySelect(chip)}
          >
            <span className={css.chipSparkle} aria-hidden="true">
              ✦
            </span>
            {chip}
          </button>
        ))}
      </div>

      <h2 className={css.recentLabel}>Recent</h2>
      <div className={css.goldRule} aria-hidden="true" />
      <ul className={css.recentList}>
        {recents.map(query => (
          <li key={query.toLowerCase()} className={css.recentItem}>
            <button
              type="button"
              className={css.recentQueryBtn}
              onClick={() => onQuerySelect(query)}
            >
              <HistoryIcon className={css.recentIcon} />
              <span className={css.recentText}>{query}</span>
            </button>
            <button
              type="button"
              className={css.recentRemoveBtn}
              onClick={() => handleRemove(query)}
              aria-label={`Remove "${query}" from recent searches`}
            >
              <RemoveIcon className={css.recentRemoveIcon} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SearchOverlayContent;
